import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import Loader from '../components/common/Loader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Camera, FileText, CheckCircle, MapPin } from 'lucide-react';
import { toZonedTime, format } from 'date-fns-tz'; // ADDED imports
import { useSettings } from '../components/context/SettingsContext';

const PageHeader = ({ title, companyName = 'Service OS' }) => {
    return (
        // ⭐ AESTHETIC UPGRADE: Blurred sticky header
        <header className="bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-800/50 shadow-sm sticky top-0 z-20">
            <div className="max-w-4xl mx-auto p-4 flex items-center">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl mr-3">
                    <FileText className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h1 className="text-xl font-black tracking-tight text-slate-800 dark:text-white">
                    {title} <span className="font-medium text-slate-400 dark:text-slate-500 mx-2">|</span> <span className="text-indigo-600 dark:text-indigo-400">{companyName}</span>
                </h1>
            </div>
        </header>
    );
};

// Updated helper function
const getCleanImageUrl = (url) => {
    if (!url) return '';
    // We now return the full URL so the secure signature is passed to the bucket
    return url;
};

const PhotoGallery = ({ photos, title, icon }) => (
    // ⭐ AESTHETIC UPGRADE: Glassmorphism rounded-3xl cards
    <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden transition-all duration-300 hover:shadow-2xl">
        <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
            <h2 className="text-xl font-bold tracking-tight text-slate-800 dark:text-white flex items-center">
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-xl mr-3 shadow-sm">
                    {React.cloneElement(icon, { className: "w-5 h-5" })}
                </div>
                {title} <span className="ml-2 text-sm font-medium px-2.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{photos.length}</span>
            </h2>
        </div>
        {photos.length > 0 ? (
            <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
                {photos.map((photo, index) => (
                    // ⭐ FIXED: Applied getCleanImageUrl to both the href and the src
                    <a key={index} href={getCleanImageUrl(photo)} target="_blank" rel="noopener noreferrer" className="aspect-w-1 aspect-h-1 block group">
                        <img 
                            src={getCleanImageUrl(photo)} 
                            alt={`${title} ${index + 1}`} 
                            // ⭐ AESTHETIC UPGRADE: rounded-2xl images with scale effect
                            className="object-cover w-full h-full rounded-2xl shadow-md transition-all duration-300 group-hover:scale-[1.03] group-hover:shadow-xl group-active:scale-[0.98]" 
                        />
                    </a>
                ))}
            </div>
        ) : (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400 italic font-medium">No photos available for this section.</div>
        )}
    </div>
);

const PublicJobPhotosPage = () => {
    const { token } = useParams();

    const [jobData, setJobData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { settings } = useSettings(); // Get settings
    const companyTimezone = settings?.timezone || 'UTC';
    
    // ⭐ NEW: Detect US Timezone
    const isUS = useMemo(() => companyTimezone.startsWith('America/'), [companyTimezone]);

    useEffect(() => {
        if (!token) {
            setError('No access token provided. This link may be invalid.');
            setLoading(false);
            return;
        }

        const fetchJobPhotos = async () => {
            setLoading(true);
            try {
                // Call the new unauthenticated public route
                const res = await api.get(`/public/jobs/${token}/photos`);
                setJobData(res.data);
            } catch (err) {
                const errorMessage = err.response?.data?.message || 'Failed to load job photos. The link might be expired or invalid.';
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchJobPhotos();
    }, [token]);

    const getFullAddress = (addr) => {
        if (!addr) return 'No Address Provided';
        const addressParts = [addr.street, addr.city, addr.postcode].filter(part => part && part.trim() !== '');
        return addressParts.length > 0 ? addressParts.join(', ') : 'No Address Provided';
    };

    // ⭐ NEW: Dynamic Date Formatting Helper
    const formatDateDisplay = useMemo(() => (dateString) => {
        if (!dateString) return 'N/A';
        const zonedDate = toZonedTime(new Date(dateString), companyTimezone);
        const formatString = isUS ? 'MMM do yyyy' : 'do MMMM yyyy';
        return format(zonedDate, formatString, { timeZone: companyTimezone });
    }, [isUS, companyTimezone]);
    // ⭐ END NEW HELPER ⭐

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
                <Loader />
                <p className="mt-4 text-slate-500 dark:text-slate-400 font-medium animate-pulse">Loading job photos...</p>
            </div>
        );
    }

    if (error || !jobData) {
        // ⭐ AESTHETIC UPGRADE: Center modal style error
        return (
            <div className="bg-slate-50 dark:bg-slate-950 min-h-screen font-sans flex flex-col">
                 <PageHeader title="Access Denied" />
                <main className="flex-1 flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 text-center border border-red-100 dark:border-red-900/50 animate-in zoom-in-95 duration-500">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <span className="text-3xl font-bold">!</span>
                        </div>
                        <p className="font-black text-2xl text-slate-900 dark:text-white mb-2 tracking-tight">Oops</p>
                        <p className="text-slate-500 dark:text-slate-400 font-medium">{error || "Job photo data not found or access is denied."}</p>
                    </div>
                </main>
                <ToastContainer position="bottom-right" theme="colored" />
            </div>
        );
    }
    
    // Check if the link is active but there are simply no photos
    const hasPhotos = (jobData.beforePhotos?.length > 0 || jobData.afterPhotos?.length > 0);

    // ⭐ MODIFIED: Use new dynamic formatter ⭐
    const jobDateFormatted = jobData.date ? formatDateDisplay(jobData.date) : 'N/A';
    // ⭐ END MODIFIED ⭐

    const jobTimeFormatted = jobData.time || 'N/A';
    
    // Duration is typically stored in minutes
    const jobDurationFormatted = jobData.duration ? `${jobData.duration} minutes` : 'N/A';


    return (
        // ⭐ AESTHETIC UPGRADE: Slate backgrounds
        <div className="bg-slate-50 dark:bg-slate-950 min-h-screen font-sans">
            <PageHeader title={`${jobData.jobType} Photos`} companyName={jobData.customerName || 'Customer'} />
            <main className="p-4 sm:p-8 max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                {/* Job Summary Card */}
                {/* ⭐ AESTHETIC UPGRADE: Glassmorphism rounded-3xl container */}
                <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-8 rounded-3xl shadow-xl space-y-6 border border-slate-200 dark:border-slate-800 transition-all duration-300 hover:shadow-2xl">
                    <h2 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">{jobData.jobType}</h2>
                    
                    {/* ⭐ MODIFIED START: Use 3-column grid for key details ⭐ */}
                    <div className="grid grid-cols-1 gap-6 text-sm text-slate-700 dark:text-slate-300 md:grid-cols-3">
                        
                        {/* Status */}
                        <div className="flex items-center p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl mr-3">
                                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Status</p>
                                <p className="font-bold text-slate-900 dark:text-white capitalize">{jobData.status.replace(/_/g, ' ')}</p>
                            </div>
                        </div>
                        
                        {/* Scheduled Date */}
                        <div className="flex items-center p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl mr-3">
                                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Scheduled Date</p>
                                <p className="font-bold text-slate-900 dark:text-white">{jobDateFormatted}</p>
                            </div>
                        </div>
                        
                        {/* Start Time & Duration merged for tighter UI */}
                        <div className="flex items-center p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl mr-3">
                                <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Time & Duration</p>
                                <p className="font-bold text-slate-900 dark:text-white">{jobTimeFormatted} <span className="text-slate-400 font-normal ml-1">({jobDurationFormatted})</span></p>
                            </div>
                        </div>
                        
                        {/* Address (Expanded to full width on mobile, taking two columns on medium/large screens) */}
                        <div className="md:col-span-3 flex items-center p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl mr-3">
                                <MapPin className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Address</p>
                                <p className="font-bold text-slate-900 dark:text-white">{getFullAddress(jobData.address)}</p>
                            </div>
                        </div>
                    </div>
                    {/* ⭐ MODIFIED END ⭐ */}
                    
                    {jobData.description && (
                         <div className="pt-6 mt-2 border-t border-slate-100 dark:border-slate-800/50">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Description</h3>
                            <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed font-medium">{jobData.description}</p>
                         </div>
                    )}
                </div>

                {hasPhotos ? (
                    <div className="space-y-8">
                        <PhotoGallery 
                            photos={jobData.beforePhotos || []} 
                            title="Before Service Photos" 
                            icon={<Camera className="text-amber-500" />}
                        />

                        <PhotoGallery 
                            photos={jobData.afterPhotos || []} 
                            title="After Service Photos" 
                            icon={<CheckCircle className="text-green-500" />}
                        />
                    </div>
                ) : (
                    // ⭐ AESTHETIC UPGRADE: Glassmorphism fallback card
                     <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-10 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 text-center">
                        <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <Camera className="text-slate-400" size={32}/>
                        </div>
                        <h3 className="text-2xl font-black tracking-tight text-slate-800 dark:text-white mb-2">No Photos Uploaded</h3>
                        <p className="text-base text-slate-500 dark:text-slate-400 font-medium max-w-sm mx-auto">
                            No "before" or "after" photos are currently available for this job. Check back later!
                        </p>
                    </div>
                )}
            </main>
            <ToastContainer position="bottom-right" theme="colored" />
        </div>
    );
};

export default PublicJobPhotosPage;